<script lang="ts">
	//Services
	import { format } from '../../../../services/i18n';
	import { localReq, postReq } from '../../../../services/http';
	//Helpers
	import { get_local_ip, real_id } from '../../../../helpers/interface_helpers';
	import { getNumberInputConfig } from '../../../../helpers/number_input_helpers';
	//Stores
	import {
		getDeviceSettings,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Design system components
	import { Button, ButtonV2, Input, InputV2, Text, toast } from '@Shelly/shelly-design-system';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { getContext, onDestroy } from 'svelte';
	import SettingGuide from '../../SettingGuide.svelte';

	export let device;

	const id = real_id(device.id);
	let settings = readDeviceSettings(id);
	$: settingsStore = getDeviceSettings(id);
	$: settings = $settingsStore ?? settings;
	let value = settings.power;
	let cache = value;
	const min = 0,
		max = 3500,
		unit = 'W';
	let loading = false;
	let localIp = get_local_ip(id);

	$: error =
		+value < min || +value > max
			? $format('error_out_of_range', {
					default: `Value ${value}${unit} is out of range. Please provide a value within the limits. (${min}${unit} — ${max}${unit})`,
					values: { value, max, min, unit },
				})
			: '';

	$: InputC = $showNewLayout ? InputV2 : Input;
	$: ButtonC = $showNewLayout ? ButtonV2 : Button;
	$: isSaveDisabled = loading || cache === value || Boolean(error);

	const setConsumption = async () => {
		if (cache !== value) {
			let res;
			value = Math.round(value);
			loading = true;
			if (localIp) {
				res = await localReq(device, `${localIp}/settings`, 'post', {
					power: value,
				});
			} else {
				res = await postReq(`/device/generic_command`, {
					id: id,
					command: `power`,
					payload: `{"power":${value}}`,
				});
			}

			if (res.isok) {
				settings.power = value;
				devices_settings.update_devices_settings(id, settings);
				toast.open({
					type: 'success',
					title: $format('power_consumption_output_on_scc_toast', {
						values: { value },
						default: `Power consumption was set to ${value}w.`,
					}),
				});
				cache = value;
			} else {
				toast.open({
					type: 'danger',
					title: $format('error_global', { default: 'Something went wrong!' }),
				});
				value = cache;
			}
			loading = false;
		}
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({
				onClick: setConsumption,
				loading,
				disabled: isSaveDisabled,
				icon: 'fas fa-save',
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

<SettingGuide
	text={$format('settings_guide_power_consumption', {
		default:
			'Assumed watts when the output is on. Use this on devices that do not measure power themselves.',
	})}
/>

<Text styleType="body-small" class="mb-s">
	{$format('set_power_consumption_output_on', {
		default: 'Set the power consumption (W) when the output is switched ON:',
	})}
</Text>
<svelte:component
	this={InputC}
	{...getNumberInputConfig()}
	class="mb-s"
	bind:value
	size="m"
	label={value}
	{error}
	{min}
	{max}
	on:enter={setConsumption}
	id="input-zpbmcbn"
	aria-labelledby="input-zpbmcbn"
/>
{#if !$showNewLayout}
	<svelte:component
		this={ButtonC}
		class="mt-s"
		{loading}
		size="m"
		width="full"
		on:click={setConsumption}
		>{$format('button_save', { default: 'Save' })}</svelte:component
	>
{/if}
